import { useState } from "react";
import { useIsDemo } from "../api/auth";
import { assetUrl } from "../api/client";
import type { ArtifactRead, RecordingDetail } from "../api/types";
import { formatBytes, formatDate, formatDuration } from "../lib/format";

// Metadata + storage/artifacts card for the detail side column.
export default function MetadataPanel({ rec }: { rec: RecordingDetail }) {
  const demo = useIsDemo();
  return (
    <section className="card meta-card">
      <div className="card-head">
        <h3>Recording</h3>
      </div>

      <dl className="kv">
        <KV label="Recorded by" value={rec.user_name} />
        <KV label="Device" value={rec.device_model} />
        {!demo && <KV label="Mode" value={rec.mode} />}
        <KV label="App version" value={rec.app_version ? `v${rec.app_version}` : null} />
        <KV label="Duration" value={formatDuration(rec.duration_ms)} />
        <KV label="Created" value={formatDate(rec.created_at)} />
        <KV label="Updated" value={formatDate(rec.updated_at)} />
        <KV label="Org" value={rec.org} />
        <KV label="Session" value={rec.session_stamp} mono />
        <KV label="Log id" value={rec.log_id} mono />
      </dl>

      <details className="storage-keys">
        <summary>Storage & artifacts</summary>
        <dl className="kv compact">
          <KV label="Prefix" value={rec.storage_prefix} mono />
          <KV label="Metadata" value={rec.metadata_key} mono />
          <KV label="data.zip" value={rec.data_zip_key} mono />
          <KV label="Video" value={rec.video_key} mono />
        </dl>
        <ArtifactsList artifacts={rec.artifacts} />
      </details>

      <MetadataDump metadata={rec.metadata_json} />
    </section>
  );
}

function ArtifactsList({ artifacts }: { artifacts: ArtifactRead[] }) {
  if (artifacts.length === 0) return <p className="muted small">No artifacts produced yet.</p>;
  return (
    <ul className="artifact-list">
      {artifacts.map((a) => (
        <li key={a.kind}>
          <a href={assetUrl(a.url)} target="_blank" rel="noreferrer">
            {a.kind}
          </a>
          <span className="muted small">
            {a.size_bytes != null ? formatBytes(a.size_bytes) : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MetadataDump({ metadata }: { metadata: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);
  if (!metadata) return null;
  return (
    <details className="meta-dump" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>metadata.json</summary>
      <pre>{JSON.stringify(metadata, null, 2)}</pre>
    </details>
  );
}

function KV({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value ?? "—"}</dd>
    </>
  );
}
